import { useCallback, useRef, useState, lazy, Suspense } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { ChevronRight, ExternalLink, Globe, Trash2 } from "lucide-react"
import { FormikProps } from "formik"
import { Integration } from "../../types"
import { useAppDispatch } from "../../common/hooks"
import { useToast } from "../../common/hooks"
import { Tabs } from "../../common/ui"
import { PrimaryBtn, DefaultBtn } from "../../common/ui/buttons"
import BBDialog from "../../common/ui/dialog"
import { RequireRole } from "../../common/validation"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import {
  Settings,
  Status,
  Install,
  Troubleshoot
} from "../../common/ui/integrations/tabs"
import { IntegrationContext } from "../../common/ui/integrations/manage"
import IntegrationStatusTag from "../../common/ui/integrations/table/components/IntegrationStatusTag"
import {
  useGetIntegrationDetailsQuery,
  useGetStatusQuery,
  useDeleteIntegrationMutation,
  useSaveIntegrationMutation
} from "../../redux/queries"
import { setPreviewModal } from "../../redux/slices/integrations"
import { normalizeUrl } from "../../common/utils"

const PreviewModal = lazy(() => import("../../common/ui/preview-modal"))

const TABS = [
  { id: "settings", label: "Settings" },
  { id: "status", label: "Status" },
  { id: "install", label: "Install" },
  { id: "troubleshoot", label: "Troubleshoot" }
]

const TYPE_LABELS: Record<string, string> = {
  standalone: "Standalone embed",
  boomtown: "LO page embed",
  api: "API embed",
  chatbot: "Website chat embed"
}

const TabSkeleton = () => (
  <div className="animate-pulse space-y-4">
    {/* General Settings card */}
    <div className="rounded-xl bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-center gap-2">
        <div className="h-4 w-4 rounded bg-gray-200" />
        <div className="h-4 w-36 rounded bg-gray-200" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="h-3 w-12 rounded bg-gray-200" />
          <div className="h-10 rounded-md border border-gray-100 bg-gray-100" />
        </div>
        <div className="space-y-2">
          <div className="h-3 w-8 rounded bg-gray-200" />
          <div className="h-10 rounded-md border border-gray-100 bg-gray-100" />
        </div>
      </div>
    </div>

    {/* Embed Type card */}
    <div className="rounded-xl bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-center gap-2">
        <div className="h-4 w-4 rounded bg-gray-200" />
        <div className="h-4 w-28 rounded bg-gray-200" />
      </div>
      <div className="grid grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className="rounded-lg border border-gray-100 bg-gray-50 p-4 space-y-3"
          >
            <div className="h-5 w-5 rounded bg-gray-200" />
            <div className="h-3.5 w-20 rounded bg-gray-200" />
            <div className="space-y-1.5">
              <div className="h-2.5 w-full rounded bg-gray-200" />
              <div className="h-2.5 w-3/4 rounded bg-gray-200" />
              <div className="h-2.5 w-1/2 rounded bg-gray-200" />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Branding + Analytics row */}
    <div className="grid grid-cols-2 gap-4">
      <div className="rounded-xl bg-white p-5 shadow-sm">
        <div className="mb-5 flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-gray-200" />
          <div className="h-4 w-20 rounded bg-gray-200" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-3 w-24 rounded bg-gray-200" />
              <div className="relative h-10 rounded-md border border-gray-100 bg-gray-100">
                <div className="absolute inset-y-0 right-2 my-auto h-6 w-6 rounded-full bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl bg-white p-5 shadow-sm">
        <div className="mb-5 flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-gray-200" />
          <div className="h-4 w-20 rounded bg-gray-200" />
        </div>
        <div className="space-y-2">
          <div className="h-3 w-36 rounded bg-gray-200" />
          <div className="h-10 rounded-md border border-gray-100 bg-gray-100" />
        </div>
      </div>
    </div>
  </div>
)

const IntegrationDetailsPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { pushSuccessToast, pushErrorToast } = useToast()

  const formIntegrationRef = useRef<FormikProps<Integration>>(null)
  const [tab, setTab] = useState("settings")
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)

  const { data: integration, isLoading: isLoadingIntegration } =
    useGetIntegrationDetailsQuery(id ?? "", { skip: !id })

  const { data: statusData = [], isLoading: isLoadingStatus } =
    useGetStatusQuery(
      { integrationID: integration?.id ?? "" },
      { skip: !integration?.id }
    )

  const [saveIntegration] = useSaveIntegrationMutation()
  const [deleteIntegration] = useDeleteIntegrationMutation()

  const onSaveSettings = useCallback(
    (values: Integration) => {
      const {
        id,
        owner,
        url,
        title,
        type,
        accent_color,
        secondary_accent_color,
        app_settings
      } = values

      const {
        mort_calc = 0,
        afford_calc = 0,
        lead_workflow,
        pricing,
        pricing_set_id,
        settings,
        rate_table_config
      } = app_settings ?? {}

      const { rateCardsConfigId } = settings ?? {}

      const isValidHexColor = (color?: string): boolean => {
        if (!color) return false
        return /^#([0-9A-F]{3}){1,2}$/i.test(color)
      }

      const normalizedAccentColor = accent_color || settings?.accent_color || ""
      const normalizedSecondaryColor =
        secondary_accent_color || settings?.secondary_accent_color || ""

      const postValues: any = {
        id,
        url: normalizeUrl(url),
        owner,
        title,
        type,
        mort_calc,
        afford_calc,
        lead_workflow,
        pricing,
        pricing_set_id,
        aboutBoxes: settings?.aboutBoxes ?? false,
        expose_api: settings?.expose_api ?? true,
        disableCalcCards: settings?.disableCalcCards ?? false,
        postUrl: settings?.postUrl ?? "",
        loReviews: settings?.loReviews || false,
        brandReviews: settings?.brandReviews ?? false,
        rateTable: settings?.rateTable ?? false,
        rateCards: settings?.rateCards ?? false,
        customParams: settings?.customParams ?? undefined,
        ga_tag: settings?.ga_tag ?? "",
        ga_mode: settings?.ga_mode ?? "",
        contactForm: settings?.contactForm ?? 0,
        rate_table_config: rate_table_config ?? "",
        rateCardsConfigId: rateCardsConfigId ?? "",
        billing_exempt: settings?.billing_exempt ?? false
      }

      if (isValidHexColor(normalizedAccentColor)) {
        postValues.accent_color = normalizedAccentColor
      }
      if (isValidHexColor(normalizedSecondaryColor)) {
        postValues.secondary_accent_color = normalizedSecondaryColor
      }

      saveIntegration(postValues)
        .unwrap()
        .then(() =>
          pushSuccessToast("Integration has been updated successfully")
        )
        .catch(() => pushErrorToast("Failed to update integration"))
    },
    [saveIntegration, pushSuccessToast, pushErrorToast]
  )

  const handleDelete = async () => {
    if (!integration?.id) return
    const res = await deleteIntegration(integration.id)
    if ("data" in res) {
      pushSuccessToast("Integration has been deleted successfully")
      navigate("/integrations")
    } else {
      pushErrorToast("Failed to delete integration")
    }
    setDeleteModalVisible(false)
  }

  const onClickPreview = useCallback(() => {
    if (integration?.app_key) {
      dispatch(setPreviewModal({ isOpen: true, appKey: integration.app_key }))
    }
  }, [dispatch, integration?.app_key])

  const isLoading = isLoadingIntegration || !integration
  const typeLabel = TYPE_LABELS[integration?.type ?? ""] ?? integration?.type

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.integrations}>
      <IntegrationContext.Provider value={{ formIntegrationRef }}>
        <div className="min-h-full">
          {/* White header */}
          <div className="bg-white border-b border-neutral-200 px-6 pt-6">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-sm text-neutral-400 mb-4">
              <Link
                to="/integrations"
                className="text-primary-600 font-medium hover:text-primary-700"
              >
                Integrations
              </Link>
              <ChevronRight size={12} className="text-neutral-400" />
              {isLoading ? (
                <div className="h-3.5 w-48 animate-pulse rounded bg-gray-200" />
              ) : (
                <span className="text-neutral-900 font-medium">
                  {integration.title}
                </span>
              )}
            </div>

            {/* Title row + actions */}
            <div className="flex items-start justify-between mb-6">
              <div>
                {isLoading ? (
                  <div className="space-y-2">
                    <div className="h-6 w-64 animate-pulse rounded bg-gray-200" />
                    <div className="h-4 w-96 animate-pulse rounded bg-gray-200" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <h1 className="text-xl font-medium text-neutral-900">
                        {integration.title}
                      </h1>
                      <IntegrationStatusTag integrationId={id ?? ""} />
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-neutral-400">
                      <Globe size={12} />
                      <a
                        href={integration.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-600 font-medium hover:text-primary-700"
                      >
                        {integration.url}
                      </a>
                      {typeLabel && (
                        <>
                          <span>·</span>
                          <span className="text-neutral-600">{typeLabel}</span>
                        </>
                      )}
                      {integration.app_key && (
                        <>
                          <span>·</span>
                          <span className="text-neutral-600">
                            App key:{" "}
                            <code className="font-mono text-xs bg-neutral-100 px-1.5 py-0.5 rounded">
                              {integration.app_key}
                            </code>
                          </span>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              {!isLoading && (
                <div className="flex items-center gap-2.5 flex-shrink-0">
                  <DefaultBtn onClick={onClickPreview}>
                    <ExternalLink size={13} className="mr-1.5" />
                    Preview
                  </DefaultBtn>
                  <button
                    onClick={() => setDeleteModalVisible(true)}
                    className="inline-flex items-center gap-1.5 h-9 px-4 text-sm font-medium text-red-600 border border-red-200 rounded hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={13} />
                    Delete Integration
                  </button>
                </div>
              )}
            </div>

            {/* Tabs */}
            <Tabs currentTab={tab} tabs={TABS} onChange={setTab} />
          </div>

          {/* Content */}
          <div className="p-6 bg-[#F1F3FA] min-h-[calc(100vh-200px)]">
            {isLoading ? (
              <TabSkeleton />
            ) : (
              <>
                {tab === "settings" && (
                  <div className="flex flex-col gap-5">
                    <Settings
                      integration={integration}
                      isLoading={isLoadingIntegration}
                      onSaveSettings={onSaveSettings}
                      open
                    />
                    <div className="flex justify-end gap-3 pt-1">
                      <DefaultBtn onClick={() => navigate("/integrations")}>
                        Cancel
                      </DefaultBtn>
                      <PrimaryBtn
                        onClick={() => formIntegrationRef.current?.submitForm()}
                      >
                        Update Integration
                      </PrimaryBtn>
                    </div>
                  </div>
                )}
                {tab === "status" && (
                  <Status
                    integration={integration}
                    statusData={statusData}
                    isLoading={isLoadingStatus}
                  />
                )}
                {tab === "install" && <Install integration={integration} />}
                {tab === "troubleshoot" && (
                  <Troubleshoot integration={integration} />
                )}
              </>
            )}
          </div>

          <Suspense fallback={null}>
            <PreviewModal
              onClose={() =>
                dispatch(setPreviewModal({ isOpen: false, appKey: "" }))
              }
            />
          </Suspense>

          <BBDialog
            open={deleteModalVisible}
            onClose={() => setDeleteModalVisible(false)}
            title="Delete integration"
            body="Are you sure you want to delete this integration?"
            okLabel="Yes, Delete"
            cancelLabel="Cancel"
            onOk={handleDelete}
            onCancel={() => setDeleteModalVisible(false)}
          />
        </div>
      </IntegrationContext.Provider>
    </RequireRole>
  )
}

export default IntegrationDetailsPage
